import React, { JSX } from 'react';
import {
  AutoFixHigh,
  BubbleChart,
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import EditDocumentIcon from '@mui/icons-material/EditDocument';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { BackstoryLogo } from 'components/ui/BackstoryLogo';
import { ContentManager } from 'components/ContentManager';
import { EmailVerificationPage, ResetPasswordPage } from 'components/EmailVerificationComponents';
import { ResumeViewer } from 'components/ui/ResumeViewer';
import { useAuth } from 'hooks/AuthContext';
import { AdminDashboard } from 'pages/AdminDashboard';
import { BetaPage } from 'pages/BetaPage';
import { CandidateChatPage } from 'pages/CandidateChatPage';
import { GenerateCandidate } from 'pages/GenerateCandidate';
import { HowItWorks } from 'pages/HowItWorks';
import { JobAnalysisPage } from 'pages/JobAnalysisPage';
import { JobsViewPage } from 'pages/JobsViewPage';
import { LoginPage } from 'pages/LoginPage';
import { CandidateDashboard } from 'pages/candidate/Dashboard';
import { CandidateProfile } from 'pages/candidate/Profile';
import { Settings } from 'pages/candidate/Settings';
import { NavigationItem } from 'types/navigation';

const LogoutPage = (): JSX.Element => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  logout().then(() => {
    navigate('/');
  });
  return <Typography variant="h4">Logging out...</Typography>;
};

export const navigationItems: NavigationItem[] = [
  {
    id: 'home',
    label: <BackstoryLogo />,
    path: '/',
    component: <HowItWorks />,
    userTypes: ['guest', 'candidate', 'employer'],
    exact: true,
  },
  {
    id: 'job-analysis',
    label: 'Job Analysis',
    path: '/job-analysis/:candidateId?/:jobId?/:stepId?',
    variant: 'fullWidth',
    icon: <WorkIcon />,
    component: <JobAnalysisPage />,
    userTypes: ['guest', 'candidate', 'employer'],
  },
  {
    id: 'chat',
    label: 'Candidate Chat',
    path: '/chat/:resumeId?',
    variant: 'fullWidth',
    icon: <ChatIcon />,
    component: <CandidateChatPage />,
    userTypes: ['guest', 'candidate', 'employer'],
  },
  {
    id: 'generate-candidate',
    label: 'Generate Candidate',
    path: '/admin/generate-candidate',
    icon: <AutoFixHigh />,
    component: <GenerateCandidate />,
    userTypes: ['admin'],
    showInNavigation: false,
    showInUserMenu: true,
    userMenuGroup: 'admin',
  },
  {
    id: 'admin-dashboard',
    label: 'Admin Dashboard',
    path: '/admin',
    icon: <DashboardIcon />,
    component: <AdminDashboard />,
    userTypes: ['admin'],
    showInNavigation: false,
    showInUserMenu: true,
    userMenuGroup: 'admin',
  },
  {
    id: 'candidate-profile',
    label: 'Profile',
    icon: <PersonIcon />,
    path: '/candidate/profile',
    component: <CandidateProfile />,
    userTypes: ['candidate'],
    userMenuGroup: 'profile',
    showInNavigation: false,
    showInUserMenu: true,
  },
  {
    id: 'candidate-dashboard',
    label: 'Dashboard',
    path: '/candidate/dashboard',
    icon: <DashboardIcon />,
    component: <CandidateDashboard />,
    userTypes: ['candidate'],
    userMenuGroup: 'profile',
    showInNavigation: false,
    showInUserMenu: true,
  },
  {
    id: 'explore-jobs',
    label: 'Jobs',
    path: '/candidate/jobs/:jobId?',
    icon: <WorkIcon />,
    component: <JobsViewPage />,
    variant: 'fullWidth',
    userTypes: ['candidate', 'guest', 'employer'],
    showInNavigation: false,
    showInUserMenu: true,
    userMenuGroup: 'profile',
  },
  {
    id: 'explore-resumes',
    label: 'Resumes',
    path: '/candidate/resumes/:resumeId?/:mode?',
    icon: <EditDocumentIcon />,
    component: <ResumeViewer />,
    variant: 'fullWidth',
    userTypes: ['candidate', 'guest', 'employer'],
    showInNavigation: false,
    showInUserMenu: true,
    userMenuGroup: 'profile',
  },
  {
    id: 'candidate-docs',
    label: 'Content',
    icon: <BubbleChart />,
    path: '/candidate/documents',
    component: <ContentManager />,
    userTypes: ['candidate'],
    userMenuGroup: 'profile',
    showInNavigation: false,
    showInUserMenu: true,
  },
  {
    id: 'candidate-settings',
    label: 'System Information',
    path: '/candidate/settings',
    icon: <SettingsIcon />,
    component: <Settings />,
    userTypes: ['candidate'],
    userMenuGroup: 'account',
    showInNavigation: false,
    showInUserMenu: true,
  },
  {
    id: 'logout',
    label: 'Logout',
    icon: <PersonIcon />,
    userTypes: ['candidate', 'employer'],
    showInNavigation: false,
    showInUserMenu: true,
    userMenuGroup: 'system',
  },
  {
    id: 'auth',
    label: 'Auth',
    userTypes: ['guest', 'candidate', 'employer'],
    showInNavigation: false,
    children: [
      {
        id: 'verify-email',
        label: 'Verify Email',
        path: '/login/verify-email',
        component: <EmailVerificationPage />,
        userTypes: ['guest', 'candidate', 'employer'],
        showInNavigation: false,
      },
      {
        id: 'reset-password',
        label: 'Reset Password',
        path: '/login/reset-password',
        component: <ResetPasswordPage />,
        userTypes: ['guest', 'candidate', 'employer'],
        showInNavigation: false,
      },
      {
        id: 'login',
        label: 'Login',
        path: '/login/:tab?',
        component: <LoginPage />,
        userTypes: ['guest', 'candidate', 'employer'],
        showInNavigation: false,
      },
      {
        id: 'logout-page',
        label: 'Logout',
        path: '/logout',
        component: <LogoutPage />,
        userTypes: ['candidate', 'employer'],
        showInNavigation: false,
      },
    ],
  },
  {
    id: 'catch-all',
    label: 'Not Found',
    path: '*',
    component: <BetaPage />,
    userTypes: ['guest', 'candidate', 'employer'],
    showInNavigation: false,
  },
];
